import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronUp, GripVertical, Play, Pause, Plus, X, HelpCircle } from "lucide-react";
import { COLORS } from "../hooks/useAudioRecorder";
import { useAuth } from "@clerk/clerk-react";
import { useProfileApi } from "../api/profile";
import { useApiClient } from "../api/client";
import { getDeviceId } from "../utils/deviceId";
import { formatPhone } from "../utils/phoneFormat";
import {
  computeSubtotal,
  computeTaxableAmount,
  computeDiscountTotal,
} from "../utils/invoiceTotals";
import PageShell from "../components/PageShell";
import AuthChip from "../components/AuthChip";
import BackLink from "../components/BackLink";
import AppFooter from "../components/AppFooter";

/* -------------------------------------------------------------------------- */
/*  Ambiguity → field mapping                                                 */
/*  Mocks return free-form ambiguity strings. We keyword-match them to fields */
/*  so that (a) field rows get a warn highlight and (b) editing a field can   */
/*  clear the matched ambiguity.                                              */
/* -------------------------------------------------------------------------- */

const FIELD_RULES = [
  { key: "customer_name", patterns: [/customer/i, /client/i, /\bname\b/i] },
  { key: "location", patterns: [/location/i, /address/i, /\bunit\b/i] },
  // line_items aggregate — catches "labor rate not stated", "materials cost
  // approximate", "lump sum unsplit", etc. Maps to all line_items in
  // ambiguityMatchesField via the "line_items" path prefix.
  { key: "line_items", patterns: [/\blabor\b/i, /material/i, /\bparts?\b/i, /vendor/i, /\bcost\b/i, /\brate\b/i, /per hour/i, /\bhours?\b/i, /lump sum/i, /all[- ]in/i, /\bsplit\b/i, /amount/i] },
];

function fieldsTouchedByAmbiguity(text) {
  const out = new Set();
  for (const rule of FIELD_RULES) {
    if (rule.patterns.some((p) => p.test(text))) out.add(rule.key);
  }
  return out;
}

// Fields that share an ambiguity with a given field — when customer.name is
// edited, also clear ambiguities mentioning "customer name", etc.
function ambiguityMatchesField(ambText, fieldKey) {
  const touched = fieldsTouchedByAmbiguity(ambText);
  // Every customer.* subfield matches the same customer_name rule — the LLM
  // produces ambiguities about "the customer" in aggregate, not per-subfield.
  if (fieldKey.startsWith("customer.")) {
    return touched.has("customer_name");
  }
  // Job.site_address inherits the legacy "location" rule. Other job.*
  // fields are not LLM-flagged (descriptions / PO numbers are user-entered).
  if (fieldKey === "job.site_address") {
    return touched.has("location");
  }
  if (fieldKey === "job.description" || fieldKey === "job.po_number") {
    return false;
  }
  // line_items.* paths share the aggregate line_items rule. Specific lines
  // can't be addressed by ambiguity text — the LLM produces line-level
  // ambiguities, not per-row ones (the row didn't exist when the ambiguity
  // was emitted; user adds it on Review). Surfaces the aggregate so the
  // user sees "something on the items list needs attention".
  if (fieldKey === "line_items" || fieldKey.startsWith("line_item:")) {
    return touched.has("line_items");
  }
  return touched.has(fieldKey);
}

/* -------------------------------------------------------------------------- */
/*  Initial state from server response                                        */
/* -------------------------------------------------------------------------- */

const mkField = (value, confidence, required = false) => ({
  value: value === undefined ? null : value,
  confidence: confidence || "high",
  edited: false,
  required,
});

// Today's date in YYYY-MM-DD (local timezone — what the contractor sees on
// the date picker). Used as the default service_date when the LLM didn't
// extract one. Local time, not UTC, so a US-Central contractor recording at
// 8pm local doesn't see tomorrow's UTC date.
function _todayISODate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Short stable id for a line item. Phase 1: client-side random, since the BE
// regenerates ids at persist time. Future: derive from server.
function _genLineId() {
  return Math.random().toString(36).slice(2, 10);
}

function buildInitialState(data) {
  const ext = data.extraction || {};
  const conf = ext.confidence || {};
  const customerExt = ext.customer || {};
  const jobExt = ext.job || {};
  return {
    customer: {
      name: mkField(
        customerExt.name ?? ext.client_name,
        conf.customer_name || conf.client_name || "high",
        true,
      ),
      phone: mkField(customerExt.phone, "high", false),
      email: mkField(customerExt.email, "high", false),
      address: mkField(customerExt.address, "high", false),
      company: mkField(customerExt.company, "high", false),
    },
    job: {
      site_address: mkField(jobExt.site_address ?? ext.location, "high", false),
      description: mkField(jobExt.description ?? ext.job_description, "high", false),
      po_number: mkField(jobExt.po_number, "high", false),
    },
    service_date: ext.service_date || _todayISODate(),
    // Deposit: null when no deposit was received. Extraction may seed a
    // deposit (e.g. "she gave me 100 upfront" from rule 13 in the prompt);
    // otherwise the DepositBlock UI starts collapsed and user opens it.
    deposit: ext.deposit && Number.isFinite(Number(ext.deposit.amount))
      ? {
          amount: Number(ext.deposit.amount),
          method: ext.deposit.method || null,
        }
      : null,
    // Per-invoice payment-terms override. null = use profile default for
    // due_date computation. When set, this beats the profile default. The
    // LLM doesn't extract terms; this is purely user-supplied via the
    // PaymentTermsBlock toggle.
    payment_terms_override: null,
    // Per-invoice customer-visible notes override. Tri-state:
    //   null   → inherit profile.invoice_footer_notes (default)
    //   ""     → explicit suppression (no NOTES block on the PDF)
    //   "text" → override with that text on this invoice only
    // The CustomerNotesBlock UI lets the user flip between these states.
    customer_visible_notes: null,
    // Flat editable list — replaces the old labor + materials + other_charges
    // triple. Each row carries its own edited / amountManuallyEdited flags
    // so the auto-amount recompute (qty × unit_price → amount unless the user
    // has touched amount directly) is per-row, not global.
    line_items: (ext.line_items || []).map((item, idx) => ({
      id: item.id || _genLineId(),
      description: item.description ?? "",
      quantity: item.quantity ?? null,
      unit: item.unit || null,
      unit_price: item.unit_price ?? null,
      amount: typeof item.amount === "number" ? item.amount : null,
      category: item.category || null,
      notes: item.notes || null,
      taxable: item.taxable !== false, // default true per Phase 1
      sort_order:
        typeof item.sort_order === "number" ? item.sort_order : idx,
      edited: false,
      // Source-of-truth flag for the auto-recompute machine. true after the
      // user types into amount directly; resets to false if they clear it.
      amountManuallyEdited:
        typeof item.amount === "number" &&
        item.quantity != null &&
        item.unit_price != null &&
        Math.abs(item.amount - item.quantity * item.unit_price) > 0.005,
    })),
    ambiguities: ext.ambiguities || [],
    transcript: data.transcript || "",
  };
}

/* -------------------------------------------------------------------------- */
/*  Issue detection — drives ActionBar and field highlights                   */
/* -------------------------------------------------------------------------- */

function fieldHasIssue(field, ambiguities, key) {
  if (field.edited) return false;
  if (field.required && (field.value === null || field.value === "")) return true;
  if (field.confidence === "low") return true;
  // Any matching ambiguity flags the field, regardless of confidence level.
  if (ambiguities.some((a) => ambiguityMatchesField(a, key))) return true;
  return false;
}

function fieldIsMissingRequired(field) {
  return field.required && !field.edited && (field.value === null || field.value === "");
}

// Returns ordered list of [fieldKey] that are flagged for the "Fix N items" walk.
function buildIssueQueue(state) {
  const out = [];
  // Customer name — required field, missing or flagged via "customer" ambiguity.
  if (fieldHasIssue(state.customer.name, state.ambiguities, "customer.name")) {
    out.push("customer.name");
  }
  // Job site — only queue when an explicit location-ish ambiguity hit.
  if (fieldHasIssue(state.job.site_address, state.ambiguities, "job.site_address")) {
    out.push("job.site_address");
  }
  // Line items — surface any row whose amount is null/zero or whose unit_price
  // is missing for an item the user clearly intended to bill (description
  // exists). Also queue an aggregate `line_items` slot when the LLM flagged
  // a line-items ambiguity (e.g. "labor rate not stated — add a line").
  const itemsAmbig = state.ambiguities.some((a) =>
    ambiguityMatchesField(a, "line_items"),
  );
  if (itemsAmbig) out.push("line_items");
  state.line_items.forEach((item, i) => {
    if (item.edited) return;
    const missingAmount = item.amount === null || item.amount === "";
    if (missingAmount && item.description && item.description.trim()) {
      out.push(`line_item:${i}:amount`);
    }
  });
  return out;
}

/* -------------------------------------------------------------------------- */
/*  Money helpers                                                              */
/* -------------------------------------------------------------------------- */

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

// Defensive amber-outline check for suspiciously low amounts. Whisper sometimes
// hears "twenty-five dollars" as "$1.25"; the LLM faithfully extracts the bad
// number and the user can ship a 75-cent invoice unless we flag it visually.
// Returns true only for values that EXIST and are below the threshold — empty,
// null, and zero do NOT trigger (those are handled by ambiguity/required flows).
const isImplausibleAmount = (value, threshold) => {
  if (value === null || value === undefined || value === "") return false;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 && n < threshold;
};

// Totals math lives in utils/invoiceTotals.js (single tested source that
// mirrors the backend). Aliased to the existing local names so the useMemos
// below are untouched.
const _computeSubtotal = computeSubtotal;
const _computeTaxableAmount = computeTaxableAmount;
const _computeDiscountTotal = computeDiscountTotal;

/* ========================================================================== */
/*  PAGE                                                                       */
/* ========================================================================== */

export default function Review() {
  const { sessionId } = useParams();
  const api = useApiClient();
  const { isLoaded: authLoaded } = useAuth();
  const { fetchProfile } = useProfileApi();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [activeFieldKey, setActiveFieldKey] = useState(null);

  // Contractor profile lookup. `null` = not yet fetched OR no profile saved
  // for this device. The Review screen always shows the BusinessOverrideCard
  // once profileLoaded is true, regardless of whether profile data exists —
  // the user fills in fields directly for this invoice.
  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Per-invoice tax state. Master toggle + mode (flat $ vs percentage) +
  // raw value + label. Hydration contract: the LLM extraction (voice)
  // always wins over profile defaults when it includes an explicit
  // enabled boolean — including enabled=true/value=0 ("Tax is 0%") and
  // enabled=false. Profile defaults only apply when the LLM is silent
  // on tax. The two source effects race on mount; `taxFromLLMRef` is the
  // synchronous handshake that prevents the profile effect from clobbering
  // an LLM decision that arrived first (or vice versa). User can edit
  // anything on this invoice; edits do not save back to the profile.
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxMode, setTaxMode] = useState("percentage"); // "flat" | "percentage"
  const [taxValue, setTaxValue] = useState(""); // raw input string, parsed per mode
  const [taxLabel, setTaxLabel] = useState("Tax");

  // Per-invoice profile field overrides. Maps fieldName -> editedValue for
  // any field the user has touched in the "Your Business" override card.
  // Untouched fields fall through to the saved profile. These edits do
  // NOT save back to the contractor profile.
  const [profileOverrides, setProfileOverrides] = useState({});

  const rowRefs = useRef({}); // fieldKey -> ref of the editable element
  const lastCommittedKeyRef = useRef(null); // for the "fix walk" advance

  // Guard: when the LLM extraction includes an explicit tax decision (enabled
  // true OR false), the contractor's spoken intent wins and profile defaults
  // must NOT clobber it. Profile fetch and session fetch race on mount; this
  // ref is the synchronous handshake between them. See hydration order note
  // on the tax-state declarations above.
  const taxFromLLMRef = useRef(false);

  useEffect(() => {
    if (!authLoaded) return;
    let mounted = true;
    fetchProfile()
      .then((p) => {
        if (!mounted) return;
        setProfile(p);
        if (!p) return;
        // If the LLM extraction has already spoken about tax (enabled true
        // or false), do not apply profile defaults — voice always wins.
        if (taxFromLLMRef.current) return;
        // Profile tax defaults live in the nested `default_tax` block that
        // Settings saves (formToPayload). The old flat default_tax_* /
        // default_tax_rate keys are pre-rewrite leftovers — never read them.
        const dt = p.default_tax;
        if (dt && dt.enabled && Number(dt.value) > 0) {
          setTaxEnabled(true);
          setTaxMode(dt.mode === "flat" ? "flat" : "percentage");
          setTaxValue(String(dt.value));
          setTaxLabel(dt.label || "Tax");
        }
      })
      .catch(() => {
        // Network / unexpected errors — leave profile null, user can still
        // proceed via the Skip path.
      })
      .finally(() => {
        if (mounted) setProfileLoaded(true);
      });
    return () => {
      mounted = false;
    };
  }, [authLoaded, fetchProfile]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    api
      .get(`/sessions/${sessionId}`)
      .then((r) => {
        if (!mounted) return;
        const initial = buildInitialState(r.data);
        setState(initial);
        // Tax hydration: the LLM (voice) always wins over profile defaults.
        // When the extraction has an explicit enabled boolean, we set the
        // guard ref so the profile-defaults effect (which races with this
        // one) will skip its tax writes — and we also clear/apply tax here
        // in case the profile effect already wrote defaults before we
        // resolved. value=0 ("Tax is 0%") hydrates as enabled with value=0;
        // enabled=false explicitly clears any profile-default tax.
        const extTax = r.data?.extraction?.tax;
        if (extTax && typeof extTax.enabled === "boolean") {
          taxFromLLMRef.current = true;
          if (extTax.enabled === true) {
            setTaxEnabled(true);
            setTaxMode(extTax.mode === "flat" ? "flat" : "percentage");
            setTaxValue(extTax.value != null ? String(extTax.value) : "");
            setTaxLabel(extTax.label || "Tax");
          } else {
            setTaxEnabled(false);
            setTaxValue("");
          }
        }
      })
      .catch((e) => {
        if (mounted) {
          const msg =
            e?.response?.status === 404
              ? "Session not found or expired."
              : e?.message || String(e);
          setError(msg);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [api, sessionId]);

  /* ---- mutations ---- */

  const commitField = useCallback((key, raw) => {
    setState((prev) => {
      if (!prev) return prev;
      const cleaned = raw === "" || raw === null ? null : raw;
      // Route customer.* paths into state.customer; everything else stays on
      // state.fields. Ambiguity clearing uses the same key so customer.email
      // also resolves any "customer_name" ambiguity (LLM produces customer-
      // level ambiguities, not per-subfield).
      if (key.startsWith("customer.")) {
        const sub = key.slice("customer.".length);
        return {
          ...prev,
          customer: {
            ...prev.customer,
            [sub]: { ...prev.customer[sub], value: cleaned, edited: true },
          },
          ambiguities: prev.ambiguities.filter(
            (a) => !ambiguityMatchesField(a, key),
          ),
        };
      }
      // Same shape for job.* — routes into state.job.
      if (key.startsWith("job.")) {
        const sub = key.slice("job.".length);
        return {
          ...prev,
          job: {
            ...prev.job,
            [sub]: { ...prev.job[sub], value: cleaned, edited: true },
          },
          ambiguities: prev.ambiguities.filter(
            (a) => !ambiguityMatchesField(a, key),
          ),
        };
      }
      // No flat-field map exists in the line_items shape — every editable
      // scalar is a customer.* / job.* path (handled above) or has its own
      // setter (service_date, deposit, line items). Any other key is unknown;
      // clear matching ambiguities but don't touch nonexistent state.fields.
      return {
        ...prev,
        ambiguities: prev.ambiguities.filter((a) => !ambiguityMatchesField(a, key)),
      };
    });
  }, []);

  // Service date is a flat ISO string on state — no edited flag needed; the
  // value itself reflects "did the user touch it". Defaults to today via
  // buildInitialState; user can pick any date with the native date input.
  const setServiceDate = useCallback((iso) => {
    setState((prev) => (prev ? { ...prev, service_date: iso || null } : prev));
  }, []);

  // Deposit setter: pass null to remove. amount stored as a number when
  // present; method is an optional free-text label ("Venmo", "Cash", etc.).
  const setDeposit = useCallback((next) => {
    setState((prev) => {
      if (!prev) return prev;
      if (!next || next.amount === null || next.amount === "" || next.amount === undefined) {
        return { ...prev, deposit: null };
      }
      return { ...prev, deposit: next };
    });
  }, []);

  // Per-invoice payment-terms override setter. Pass null to clear back to
  // the profile default; pass {days, label} to override. days=0 means
  // "Due on receipt"; label is an optional override of the rendered string.
  const setPaymentTermsOverride = useCallback((next) => {
    setState((prev) => {
      if (!prev) return prev;
      if (!next || next.days === null || next.days === "" || next.days === undefined) {
        return { ...prev, payment_terms_override: null };
      }
      return { ...prev, payment_terms_override: next };
    });
  }, []);

  // Customer-visible notes setter. Tri-state per the comment on state:
  //   null   → inherit from profile.invoice_footer_notes
  //   ""     → explicit "no notes on this invoice"
  //   "text" → override with that text
  // Callers pass null to reset to inherit; the UI's "customize for this
  // invoice" flow passes "" first (entering override mode with empty text),
  // then "text" once the user types.
  const setCustomerVisibleNotes = useCallback((next) => {
    setState((prev) => (prev ? { ...prev, customer_visible_notes: next } : prev));
  }, []);

  /* ---- Line item mutations ---- */

  // commitLineItem(index, patch) updates a single row with shallow merge.
  // The auto-amount machine fires from here: editing quantity or unit_price
  // recomputes `amount` ONLY when the row's amountManuallyEdited flag is
  // false; editing amount directly flips the flag to true (or back to false
  // when the user clears amount entirely).
  const commitLineItem = useCallback((index, patch) => {
    setState((prev) => {
      if (!prev) return prev;
      const items = prev.line_items.map((it, i) => {
        if (i !== index) return it;
        let next = { ...it, ...patch, edited: true };
        if ("amount" in patch) {
          // Direct amount edit. Empty → user wants the recompute back; any
          // explicit number (including 0) → they want this value verbatim.
          next.amountManuallyEdited =
            patch.amount !== null && patch.amount !== "" && patch.amount !== undefined;
        } else if ("quantity" in patch || "unit_price" in patch) {
          if (!next.amountManuallyEdited) {
            const q = Number(next.quantity);
            const up = Number(next.unit_price);
            if (Number.isFinite(q) && Number.isFinite(up)) {
              next.amount = +(q * up).toFixed(2);
            }
          }
        }
        return next;
      });
      // Editing any line item clears the aggregate line_items ambiguity (a
      // user who's filled in lines has addressed the "needs attention" flag).
      const ambiguities = prev.ambiguities.filter(
        (a) => !ambiguityMatchesField(a, "line_items"),
      );
      return { ...prev, line_items: items, ambiguities };
    });
  }, []);

  const addLineItem = useCallback(() => {
    setState((prev) => {
      if (!prev) return prev;
      const items = [
        ...prev.line_items,
        {
          id: _genLineId(),
          description: "",
          quantity: null,
          unit: null,
          unit_price: null,
          amount: null,
          category: null,
          notes: null,
          taxable: true,
          sort_order: prev.line_items.length,
          edited: true,
          amountManuallyEdited: false,
        },
      ];
      return { ...prev, line_items: items };
    });
  }, []);

  const removeLineItem = useCallback((index) => {
    setState((prev) => {
      if (!prev) return prev;
      const items = prev.line_items
        .filter((_, i) => i !== index)
        .map((it, idx) => ({ ...it, sort_order: idx }));
      return { ...prev, line_items: items };
    });
  }, []);

  // Move a single row from `from` index to `to` index. Used by the ▲/▼
  // arrow buttons (always available) and the desktop drag-handle. Bounds-
  // checked so the buttons can be wired up without per-row enable logic.
  const reorderLineItem = useCallback((from, to) => {
    setState((prev) => {
      if (!prev) return prev;
      const n = prev.line_items.length;
      if (
        from < 0 || from >= n ||
        to < 0 || to >= n ||
        from === to
      ) {
        return prev;
      }
      const items = [...prev.line_items];
      const [moved] = items.splice(from, 1);
      items.splice(to, 0, moved);
      items.forEach((it, idx) => { it.sort_order = idx; });
      return { ...prev, line_items: items };
    });
  }, []);

  // Discount is modeled as a single negative-amount line item (category
  // "discount") so the wire payload + six-value math reconciliation are
  // unchanged — but it's edited here in the totals area, not the line-items
  // list (which hides negative lines). Pass a positive amount to set; ""/0/null
  // to clear. Reuses the first existing negative line if one is present.
  const setDiscount = useCallback((rawAmount) => {
    setState((prev) => {
      if (!prev) return prev;
      const n =
        rawAmount === "" || rawAmount == null ? null : Math.abs(Number(rawAmount));
      // Consolidate: drop EVERY existing discount (negative) line, then re-add
      // a single one. The totals-area editor shows the summed discount, so the
      // edit must replace the whole thing — otherwise editing the displayed sum
      // would only touch one of several negative lines and silently diverge
      // (e.g. extraction emitted two "10% off" + "$25 off" deductions).
      const positives = prev.line_items.filter((it) => Number(it.amount) >= 0);
      if (n === null || !Number.isFinite(n) || n === 0) {
        return {
          ...prev,
          line_items: positives.map((it, i) => ({ ...it, sort_order: i })),
        };
      }
      // Keep a prior discount line's id/description if one existed.
      const prior = prev.line_items.find((it) => Number(it.amount) < 0);
      const items = [
        ...positives,
        {
          id: prior?.id || _genLineId(),
          description: prior?.description || "Discount",
          quantity: null,
          unit: null,
          unit_price: null,
          amount: -n,
          category: prior?.category || "discount",
          notes: null,
          taxable: true,
          sort_order: positives.length,
          edited: true,
          amountManuallyEdited: true,
        },
      ].map((it, i) => ({ ...it, sort_order: i }));
      return { ...prev, line_items: items };
    });
  }, []);

  /* ---- Fix-walk: scroll & focus ---- */

  const focusFieldKey = useCallback((key) => {
    if (!key) return;
    setActiveFieldKey(key);
    setTimeout(() => {
      let testid;
      if (key.startsWith("line_item:")) {
        const parts = key.split(":"); // line_item:<idx>:<sub>
        const idx = parts[1];
        const sub = parts[2] || "amount";
        testid = `review-line-${idx}-${sub}`;
      } else if (key === "line_items") {
        // Aggregate — focus the first line's amount input as a reasonable
        // landing spot, or the Add-line button if there are no lines yet.
        testid = "review-line-0-amount";
      } else {
        testid = `review-field-${key}`;
      }
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (!el) return;
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (e) {
        /* ignore */
      }
      try {
        el.focus({ preventScroll: true });
        // Select-all so the next keystroke replaces the value, not appends.
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      } catch (e) {
        /* ignore */
      }
    }, 100);
  }, []);

  const issueQueue = useMemo(() => (state ? buildIssueQueue(state) : []), [state]);
  const missingRequiredCount = useMemo(() => {
    if (!state) return 0;
    // Customer.name is the only required field in the line_items shape. The
    // old flat `state.fields` map no longer exists post-rewrite; add other
    // required fields here (under state.customer / state.job) if they appear.
    let n = 0;
    if (fieldIsMissingRequired(state.customer.name)) n += 1;
    return n;
  }, [state]);

  // All six BE-reconciled totals computed reactively from line_items + tax.
  // Mirrors backend math (server.py _compute_*): tax applies to
  // taxable_amount, NOT subtotal. balance_due nets out the deposit (and any
  // future payments, currently empty in Phase 1).
  const subtotal = useMemo(
    () => (state ? _computeSubtotal(state.line_items) : 0),
    [state],
  );
  const taxableAmount = useMemo(
    () => (state ? _computeTaxableAmount(state.line_items) : 0),
    [state],
  );
  const discountTotal = useMemo(
    () => (state ? _computeDiscountTotal(state.line_items) : 0),
    [state],
  );
  const taxAmount = useMemo(() => {
    if (!taxEnabled) return 0;
    const v = Number(taxValue);
    if (!Number.isFinite(v) || v <= 0) return 0;
    if (taxMode === "flat") return +v.toFixed(2);
    return +(taxableAmount * (v / 100)).toFixed(2);
  }, [taxEnabled, taxMode, taxValue, taxableAmount]);
  const totalDue = useMemo(
    () => +(subtotal + taxAmount).toFixed(2),
    [subtotal, taxAmount],
  );
  // Deposit reduces balance_due (server.py _compute_balance_due mirror).
  // Payments stay [] in Phase 1, so the deposit is the only paid amount.
  const depositAmount = useMemo(() => {
    const a = state?.deposit?.amount;
    return Number.isFinite(Number(a)) ? Number(a) : 0;
  }, [state]);
  const balanceDue = useMemo(
    () => +(totalDue - depositAmount).toFixed(2),
    [totalDue, depositAmount],
  );

  // Implausibility roll-up. Per-line outlines beat the total outline (one
  // signal beats two). amount<$1 (non-discount) and unit_price<$10 when
  // category=labor are the per-line triggers; total<$10 catches sums that
  // round to lunch money without any single line tripping the per-line check.
  const anyLineImplausible = useMemo(() => {
    if (!state) return false;
    return state.line_items.some((it) => {
      if (it.amount > 0 && isImplausibleAmount(it.amount, 1)) return true;
      if (
        String(it.category || "").toLowerCase() === "labor" &&
        isImplausibleAmount(it.unit_price, 10)
      ) {
        return true;
      }
      return false;
    });
  }, [state]);
  const totalImplausible =
    totalDue > 0 && totalDue < 10 && !anyLineImplausible;

  // After an edit, advance the focus walk if user is on an active field.
  // We mark the just-committed key with a ref, then react to the resulting
  // state change in a useEffect (cleaner than chaining setStates).
  const onAfterCommit = useCallback((key) => {
    lastCommittedKeyRef.current = key;
  }, []);

  useEffect(() => {
    if (!state) return;
    const just = lastCommittedKeyRef.current;
    if (!just) return;
    if (activeFieldKey !== just) {
      lastCommittedKeyRef.current = null;
      return;
    }
    lastCommittedKeyRef.current = null;
    const queue = buildIssueQueue(state);
    const target = queue[0];
    if (target) focusFieldKey(target);
    else setActiveFieldKey(null);
  }, [state, activeFieldKey, focusFieldKey]);

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const navigate = useNavigate();

  const onGeneratePdf = useCallback(async () => {
    if (!state) return;
    // Same two rules the backend enforces at finalize (no empty invoice, no
    // negative total) — caught here so the contractor gets the message
    // without a round trip. A discount-only invoice counts as empty.
    if (!state.line_items.some((li) => Number(li.amount) > 0)) {
      setGenerateError("Add at least one charge before generating the invoice.");
      return;
    }
    if (totalDue < 0) {
      setGenerateError("The discount is larger than the charges — the total can't be negative.");
      return;
    }
    setGenerateError("");
    setGenerating(true);
    const final = serializeForFinalize({
      state,
      subtotal,
      taxableAmount,
      discountTotal,
      taxAmount,
      totalDue,
      balanceDue,
      taxEnabled,
      taxMode,
      taxValue,
      taxLabel,
    });
    // Carry the profile's default payment terms through finalize. Anonymous
    // users have no server-side profile, so without this their Settings
    // default (e.g. Net 15) would be dropped and the invoice would fall back
    // to "Due on receipt". Harmless for signed-in users (same value the server
    // profile already has). Whitelisted on the backend.
    const overridesOut = { ...profileOverrides };
    if (profile?.default_payment_terms) {
      overridesOut.default_payment_terms = profile.default_payment_terms;
    }
    // Invoice number is assigned server-side at finalize (YYYYMMDD-NNN).
    try {
      const r = await api.post(`/sessions/${sessionId}/finalize`, {
        ...final,
        device_id: getDeviceId(),
        profile_overrides:
          Object.keys(overridesOut).length > 0 ? overridesOut : null,
      });
      navigate(`/success/${sessionId}`, { state: r.data });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to generate PDF.";
      setGenerateError(String(msg));
      setGenerating(false);
    }
  }, [
    api, state, sessionId, navigate, profileOverrides, profile,
    subtotal, taxableAmount, discountTotal, taxAmount, totalDue, balanceDue,
    taxEnabled, taxMode, taxValue, taxLabel,
  ]);

  /* ---- render ---- */

  if (loading) {
    return (
      <ReviewShell sessionId={sessionId}>
        <div
          data-testid="review-loading"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            color: COLORS.faint,
            paddingTop: 40,
            textAlign: "center",
          }}
        >
          loading…
        </div>
      </ReviewShell>
    );
  }
  if (error) {
    return (
      <ReviewShell sessionId={sessionId}>
        <div
          data-testid="review-error"
          role="alert"
          style={{
            padding: "14px 16px",
            borderRadius: 12,
            border: `1px solid ${COLORS.red}`,
            backgroundColor: "rgba(229,75,60,0.08)",
            color: "#ffb3ab",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            lineHeight: 1.5,
            marginTop: 16,
          }}
        >
          {error}
        </div>
      </ReviewShell>
    );
  }
  if (!state) return null;

  const ambiguityCount = state.ambiguities.length;
  const allCleanForPdf = ambiguityCount === 0 && missingRequiredCount === 0;

  // Always show the override card once we've finished the profile fetch.
  // Empty fields render as blank inputs the user can fill in for this
  // invoice; permanent profile creation lives on /settings.
  const showOverrideCard = profileLoaded;

  return (
    <ReviewShell sessionId={sessionId}>
      <TranscriptStrip
        sessionId={sessionId}
        transcript={state.transcript}
      />

      {ambiguityCount > 0 && <AmbiguityBanner ambiguities={state.ambiguities} />}

      {showOverrideCard && (
        <BusinessOverrideCard
          profile={profile}
          overrides={profileOverrides}
          onChange={(key, value) =>
            setProfileOverrides((prev) => {
              const next = { ...prev, [key]: value };
              return next;
            })
          }
          onReset={(key) =>
            setProfileOverrides((prev) => {
              const next = { ...prev };
              delete next[key];
              return next;
            })
          }
        />
      )}

      <InvoicePreview
        state={state}
        rowRefs={rowRefs}
        activeFieldKey={activeFieldKey}
        onCommitField={(k, v) => {
          commitField(k, v);
          onAfterCommit(k);
        }}
        onCommitLineItem={(i, patch) => {
          commitLineItem(i, patch);
          // Walk advances on amount edits (the primary "did the user fix it?"
          // signal); other patches don't change the queue meaningfully.
          if ("amount" in patch) onAfterCommit(`line_item:${i}:amount`);
        }}
        onAddLineItem={addLineItem}
        onRemoveLineItem={removeLineItem}
        onReorderLineItem={reorderLineItem}
        onChangeServiceDate={setServiceDate}
        onChangeDeposit={setDeposit}
        onChangeDiscount={setDiscount}
        onChangePaymentTermsOverride={setPaymentTermsOverride}
        profileDefaultPaymentTerms={profile?.default_payment_terms || null}
        onChangeCustomerVisibleNotes={setCustomerVisibleNotes}
        profileFooterNotes={profile?.invoice_footer_notes || ""}
        subtotal={subtotal}
        discountTotal={discountTotal}
        taxableAmount={taxableAmount}
        taxEnabled={taxEnabled}
        taxMode={taxMode}
        taxValue={taxValue}
        taxLabel={taxLabel}
        taxAmount={taxAmount}
        onToggleTaxEnabled={setTaxEnabled}
        onSwitchTaxMode={(m) => { setTaxMode(m); setTaxValue(""); }}
        onTaxValueChange={setTaxValue}
        onTaxLabelChange={setTaxLabel}
        totalDue={totalDue}
        balanceDue={balanceDue}
        totalImplausible={totalImplausible}
      />

      {/* Spacer so sticky bar doesn't cover content */}
      <div style={{ height: 110 }} />

      <ActionBar
        state={state}
        issueCount={ambiguityCount + missingRequiredCount}
        missingRequiredCount={missingRequiredCount}
        allClean={allCleanForPdf}
        generating={generating}
        generateError={generateError}
        onFix={() => {
          if (issueQueue.length > 0) focusFieldKey(issueQueue[0]);
        }}
        onSkip={() => {
          setState((prev) => (prev ? { ...prev, ambiguities: [] } : prev));
        }}
        onGenerate={onGeneratePdf}
      />
    </ReviewShell>
  );
}

// Build the /finalize payload in the new line_items shape. Mirrors the
// backend FinalizeRequest fields exactly so the BE math integrity check
// (which compares all six FE-reported totals to its own re-computed ones)
// accepts the request. Customer/job/deposit slots reserved here so G4/G5
// can add deposit + payment_terms overrides without touching the contract.
function serializeForFinalize({
  state,
  subtotal,
  taxableAmount,
  discountTotal,
  taxAmount,
  totalDue,
  balanceDue,
  taxEnabled,
  taxMode,
  taxValue,
  taxLabel,
}) {
  const trim = (v) => (typeof v === "string" ? v.trim() : v);
  const nullIf = (v) => {
    const t = trim(v);
    return t === "" || t == null ? null : t;
  };
  return {
    customer: {
      name: nullIf(state.customer.name.value),
      phone: nullIf(state.customer.phone.value),
      email: nullIf(state.customer.email.value),
      address: nullIf(state.customer.address.value),
      company: nullIf(state.customer.company.value),
    },
    job: {
      site_address: nullIf(state.job.site_address.value),
      description: nullIf(state.job.description.value),
      po_number: nullIf(state.job.po_number.value),
      name: null,
      project_number: null,
    },
    service_date: state.service_date || null,
    line_items: state.line_items.map((it, idx) => ({
      id: it.id,
      description: trim(it.description) || "",
      quantity: it.quantity === null || it.quantity === "" ? null : Number(it.quantity),
      unit: nullIf(it.unit),
      unit_price:
        it.unit_price === null || it.unit_price === "" ? null : Number(it.unit_price),
      amount: it.amount === null || it.amount === "" ? 0 : Number(it.amount),
      category: nullIf(it.category),
      notes: nullIf(it.notes),
      taxable: it.taxable !== false,
      sort_order: typeof it.sort_order === "number" ? it.sort_order : idx,
    })),
    tax: taxEnabled
      ? {
          enabled: true,
          mode: taxMode,
          value: Number(taxValue) || null,
          label: (taxLabel || "Tax").trim() || "Tax",
        }
      : { enabled: false, mode: null, value: null, label: null },
    deposit: state.deposit
      ? {
          amount: Number(state.deposit.amount),
          method: nullIf(state.deposit.method),
          received_at: null,
          notes: null,
        }
      : null,
    payments: [],  // Phase 1: never populated client-side; future payments UI.
    subtotal,
    discount_total: discountTotal,
    taxable_amount: taxableAmount,
    tax_amount: taxAmount,
    total_due: totalDue,
    balance_due: balanceDue,
    // customer_visible_notes is tri-state on Review (null = inherit profile
    // footer notes, "" = explicit suppression, "text" = override). Send the
    // raw value as-is; PDF and detail views check for null vs "" themselves.
    customer_visible_notes: state.customer_visible_notes,
    internal_notes: null,
    payment_terms_override:
      state.payment_terms_override && state.payment_terms_override.days !== null
        ? {
            days: Number(state.payment_terms_override.days),
            label: nullIf(state.payment_terms_override.label),
          }
        : null,
  };
}

/* ========================================================================== */
/*  Shell                                                                      */
/* ========================================================================== */

const ReviewShell = ({ sessionId, children }) => (
  <PageShell testId="review-page" padding="20px 18px 24px" maxWidth={680}>
    <div style={{ position: "relative" }}>
    <style>{`
      [data-ledger-editable]:empty::before {
        content: attr(data-placeholder);
        color: ${COLORS.warn};
        font-style: italic;
        opacity: 0.85;
        pointer-events: none;
        font-weight: 400;
      }
      [data-ledger-editable]:focus::before {
        opacity: 0.45;
      }
      [data-ledger-remove-row]:hover {
        color: ${COLORS.red};
      }
      [data-ledger-mode-btn] {
        transition: background-color 160ms ease, color 160ms ease;
      }
    `}</style>
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 14,
      }}
    >
      <BackLink to="/" label="Home" testId="review-back-link" />
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <AuthChip />
        <div
          data-testid="review-session-id"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            color: COLORS.fainter,
            textTransform: "uppercase",
          }}
        >
          SESSION · {String(sessionId || "").toUpperCase()}
        </div>
      </div>
    </header>
    <h1
      style={{
        fontFamily: "'Instrument Serif', Georgia, serif",
        fontStyle: "italic",
        fontSize: 30,
        margin: "0 0 14px 0",
        color: COLORS.text,
      }}
    >
      Review<span style={{ color: COLORS.yellow }}>.</span>
    </h1>
    {children}
    <AppFooter />
    </div>
  </PageShell>
);

/* ========================================================================== */
/*  TranscriptStrip                                                            */
/* ========================================================================== */

const TranscriptStrip = ({ sessionId, transcript }) => {
  const api = useApiClient();
  const [expanded, setExpanded] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioReady, setAudioReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  // Hide the whole strip when there's no transcript to point at — anonymous
  // races, expired sessions, etc. Better than a toggle that opens to nothing.
  const hasTranscript = !!(transcript && transcript.trim());

  // Try to fetch the audio bytes — it may have expired. Guarded so we don't
  // pointlessly hit /audio when we're not even going to render the strip.
  useEffect(() => {
    if (!hasTranscript) return;
    let revoked = false;
    let url = null;
    api
      .get(`/sessions/${sessionId}/audio`, { responseType: "blob" })
      .then((r) => {
        if (revoked) return;
        url = URL.createObjectURL(r.data);
        setAudioUrl(url);
        setAudioReady(true);
      })
      .catch(() => setAudioReady(false));
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [api, sessionId, hasTranscript]);

  if (!hasTranscript) return null;

  const togglePlay = (e) => {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  };

  return (
    <div
      data-testid="review-transcript"
      onClick={() => setExpanded((v) => !v)}
      style={{
        backgroundColor: COLORS.panel,
        border: `1px solid ${COLORS.rule}`,
        borderRadius: 12,
        padding: "10px 12px",
        marginBottom: 14,
        cursor: "pointer",
        transition: "border-color 160ms ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {audioReady ? (
          <button
            data-testid="review-replay-btn"
            onClick={togglePlay}
            aria-label={playing ? "Pause" : "Replay audio"}
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              border: `1px solid ${COLORS.yellow}`,
              backgroundColor: "transparent",
              color: COLORS.yellow,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
              flex: "0 0 auto",
            }}
          >
            {playing ? <Pause size={12} fill={COLORS.yellow} /> : <Play size={12} fill={COLORS.yellow} />}
          </button>
        ) : (
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              border: `1px dashed ${COLORS.fainter}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "0 0 auto",
            }}
          >
            <Play size={12} color={COLORS.fainter} />
          </span>
        )}
        <span
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.08em",
            color: COLORS.faint,
            flex: 1,
          }}
        >
          // what I heard
        </span>
        <ChevronDown
          size={14}
          color={COLORS.faint}
          style={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms ease",
            flex: "0 0 auto",
          }}
        />
      </div>
      {expanded && (
        <div
          data-testid="review-transcript-full"
          style={{
            marginTop: 10,
            padding: "10px 12px",
            border: `1px dashed ${COLORS.rule}`,
            borderRadius: 8,
            backgroundColor: "#141210",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            lineHeight: 1.55,
            color: "#a8a292",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {transcript}
        </div>
      )}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          style={{ display: "none" }}
        />
      )}
    </div>
  );
};

/* ========================================================================== */
/*  AmbiguityBanner                                                            */
/* ========================================================================== */

/* ========================================================================== */
/*  BusinessOverrideCard — collapsible per-invoice profile field overrides.   */
/*  Edits NEVER save back to the contractor profile. Only fields the user     */
/*  has touched get sent in the finalize POST as `profile_overrides`.         */
/* ========================================================================== */

const _OVERRIDABLE_FIELDS = [
  { key: "business_name", label: "Business name", placeholder: "e.g. Smith Plumbing" },
  { key: "contractor_name", label: "Your name", placeholder: "e.g. Mike Johnson" },
  { key: "phone", label: "Phone", placeholder: "(555) 012-3456", inputMode: "tel" },
  { key: "email", label: "Email", placeholder: "you@business.com", inputMode: "email" },
  { key: "address", label: "Address", placeholder: "Optional" },
  { key: "license_number", label: "License #", placeholder: "Optional" },
  { key: "tagline", label: "Tagline", placeholder: "Optional" },
  {
    key: "payment_methods",
    label: "Payment methods",
    placeholder: "e.g. Cash, check, Venmo @handle",
    multiline: true,
  },
  // NOTE: invoice_footer_notes is intentionally NOT overridable here — it has
  // its own dedicated per-invoice control (CustomerNotesBlock) with tri-state
  // inherit/suppress/override semantics. It's edited on /settings as the
  // profile default and flows to the PDF from the profile snapshot otherwise.
];

const BusinessOverrideCard = ({ profile, overrides, onChange, onReset }) => {
  const [expanded, setExpanded] = useState(false);
  // Effective value for a field = override if touched, else saved profile value.
  const effective = (key) =>
    overrides[key] !== undefined ? overrides[key] : (profile?.[key] || "");

  // Logo is view-only here — it appears on the PDF but is uploaded/removed on
  // /settings (no per-invoice logo override). The raw base64 carries no
  // "data:" prefix (see generateInvoicePdf _drawLogo), so we rebuild the URL.
  const logo = profile?.logo;
  const logoSrc =
    logo && logo.data
      ? `data:${logo.mime_type || "image/png"};base64,${logo.data}`
      : null;

  // Summary line for the collapsed state. Picks first 2-3 non-empty
  // identifying fields, separated by middots.
  const summary = (() => {
    const bits = [
      effective("business_name"),
      effective("contractor_name"),
      effective("phone") || effective("email"),
    ]
      .map((s) => (s || "").toString().trim())
      .filter(Boolean);
    return bits.length ? bits.join("  ·  ") : "Tap to add business details";
  })();

  const editedCount = Object.keys(overrides).length;

  return (
    <section
      data-testid="review-business-override"
      style={{
        marginBottom: 14,
        backgroundColor: COLORS.panel,
        border: `1px solid ${COLORS.rule}`,
        borderRadius: 14,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        data-testid="review-business-override-toggle"
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          cursor: "pointer",
          color: COLORS.text,
          textAlign: "left",
        }}
      >
        {logoSrc && (
          <img
            src={logoSrc}
            alt="Business logo"
            data-testid="review-business-override-logo"
            style={{
              width: 40,
              height: 40,
              objectFit: "contain",
              borderRadius: 6,
              backgroundColor: "#f4f1ea",
              flex: "0 0 auto",
            }}
          />
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: COLORS.faint,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            Your Business
            {editedCount > 0 && (
              <span
                data-testid="review-business-override-edited-pill"
                style={{
                  fontSize: 9,
                  color: COLORS.yellow,
                  letterSpacing: "0.14em",
                  border: `1px solid ${COLORS.yellow}`,
                  borderRadius: 999,
                  padding: "1px 6px",
                }}
              >
                EDITED
              </span>
            )}
          </div>
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              color: COLORS.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {summary}
          </div>
        </div>
        <ChevronDown
          size={16}
          color={COLORS.faint}
          style={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 180ms ease",
            flex: "0 0 auto",
          }}
        />
      </button>

      {expanded && (
        <div
          data-testid="review-business-override-fields"
          style={{
            borderTop: `1px dashed ${COLORS.rule}`,
            padding: "14px 16px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10,
              color: COLORS.faint,
              letterSpacing: "0.04em",
              lineHeight: 1.5,
              marginBottom: 4,
            }}
          >
            // changes apply to this invoice only — your saved profile won't change
          </div>
          {logoSrc && (
            <div
              data-testid="review-business-override-logo-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 4,
              }}
            >
              <img
                src={logoSrc}
                alt="Business logo"
                style={{
                  width: 48,
                  height: 48,
                  objectFit: "contain",
                  borderRadius: 6,
                  backgroundColor: "#f4f1ea",
                  flex: "0 0 auto",
                }}
              />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 9,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: COLORS.faint,
                }}
              >
                Logo — change in Settings
              </span>
            </div>
          )}
          {_OVERRIDABLE_FIELDS.map((f) => {
            const value = effective(f.key);
            const isEdited = overrides[f.key] !== undefined;
            return (
              <label
                key={f.key}
                style={{
                  display: "block",
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 9,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: COLORS.faint,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 5,
                  }}
                >
                  <span>{f.label}</span>
                  {isEdited && (
                    <button
                      type="button"
                      onClick={() => onReset(f.key)}
                      data-testid={`review-business-override-reset-${f.key}`}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        color: COLORS.yellow,
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: 9,
                        letterSpacing: "0.16em",
                        cursor: "pointer",
                        textTransform: "uppercase",
                      }}
                    >
                      Reset
                    </button>
                  )}
                </div>
                {f.multiline ? (
                  <textarea
                    rows={2}
                    value={value}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    data-testid={`review-business-override-${f.key}`}
                    style={{
                      width: "100%",
                      backgroundColor: "#1f1c17",
                      border: `1px solid ${isEdited ? COLORS.yellow : COLORS.rule}`,
                      borderRadius: 8,
                      padding: "8px 10px",
                      color: COLORS.text,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 13,
                      lineHeight: 1.4,
                      resize: "vertical",
                      minHeight: 44,
                      outline: "none",
                      boxSizing: "border-box",
                      textTransform: "none",
                      letterSpacing: "normal",
                    }}
                  />
                ) : (
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    inputMode={f.inputMode}
                    data-testid={`review-business-override-${f.key}`}
                    style={{
                      width: "100%",
                      backgroundColor: "#1f1c17",
                      border: `1px solid ${isEdited ? COLORS.yellow : COLORS.rule}`,
                      borderRadius: 8,
                      padding: "8px 10px",
                      color: COLORS.text,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 13,
                      outline: "none",
                      boxSizing: "border-box",
                      textTransform: "none",
                      letterSpacing: "normal",
                    }}
                  />
                )}
              </label>
            );
          })}
        </div>
      )}
    </section>
  );
};

const AmbiguityBanner = ({ ambiguities }) => (
  <div
    data-testid="review-ambiguity-banner"
    style={{
      backgroundColor: COLORS.warnBg,
      border: `1px solid ${COLORS.warn}`,
      borderRadius: 12,
      padding: "12px 14px",
      marginBottom: 14,
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
    }}
  >
    <AlertTriangle size={18} color={COLORS.warn} style={{ flex: "0 0 auto", marginTop: 1 }} />
    <div style={{ flex: 1 }}>
      <div
        data-testid="review-ambiguity-title"
        style={{
          fontFamily: "'Inter', sans-serif",
          fontWeight: 700,
          fontSize: 13,
          color: COLORS.warn,
          letterSpacing: "0.01em",
        }}
      >
        {ambiguities.length} thing{ambiguities.length === 1 ? "" : "s"} to confirm
      </div>
      <div
        data-testid="review-ambiguity-desc"
        style={{
          marginTop: 4,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          color: "#c8b873",
          lineHeight: 1.5,
        }}
      >
        {ambiguities.join("  ·  ")}
      </div>
    </div>
  </div>
);

/* ========================================================================== */
/*  InvoicePreview                                                             */
/* ========================================================================== */

const InvoicePreview = ({
  state,
  rowRefs,
  activeFieldKey,
  onCommitField,
  onCommitLineItem,
  onAddLineItem,
  onRemoveLineItem,
  onReorderLineItem,
  onChangeServiceDate,
  onChangeDeposit,
  onChangeDiscount,
  onChangePaymentTermsOverride,
  profileDefaultPaymentTerms,
  onChangeCustomerVisibleNotes,
  profileFooterNotes,
  subtotal,
  discountTotal,
  taxableAmount,
  taxEnabled,
  taxMode,
  taxValue,
  taxLabel,
  taxAmount,
  onToggleTaxEnabled,
  onSwitchTaxMode,
  onTaxValueChange,
  onTaxLabelChange,
  totalDue,
  balanceDue,
  totalImplausible,
}) => (
  <section
    data-testid="review-invoice"
    style={{
      backgroundColor: COLORS.panel,
      border: `1px solid ${COLORS.rule}`,
      borderRadius: 16,
      padding: "18px 18px 0",
      overflow: "hidden",
    }}
  >
    <CustomerBlock
      customer={state.customer}
      ambiguities={state.ambiguities}
      rowRefs={rowRefs}
      activeFieldKey={activeFieldKey}
      onCommit={onCommitField}
    />

    <SectionDivider />

    <JobBlock
      job={state.job}
      serviceDate={state.service_date}
      ambiguities={state.ambiguities}
      rowRefs={rowRefs}
      activeFieldKey={activeFieldKey}
      onCommit={onCommitField}
      onChangeServiceDate={onChangeServiceDate}
    />

    <SectionDivider />

    <LineItemsBlock
      lineItems={state.line_items}
      rowRefs={rowRefs}
      activeFieldKey={activeFieldKey}
      onCommit={onCommitLineItem}
      onAdd={onAddLineItem}
      onRemove={onRemoveLineItem}
      onReorder={onReorderLineItem}
    />

    <DepositBlock deposit={state.deposit} onChange={onChangeDeposit} />

    <SubtotalTaxBlock
      subtotal={subtotal}
      discountTotal={discountTotal}
      onDiscountChange={onChangeDiscount}
      depositAmount={state.deposit?.amount ?? 0}
      taxEnabled={taxEnabled}
      taxMode={taxMode}
      taxValue={taxValue}
      taxLabel={taxLabel}
      taxAmount={taxAmount}
      totalDue={totalDue}
      onToggleTaxEnabled={onToggleTaxEnabled}
      onSwitchTaxMode={onSwitchTaxMode}
      onTaxValueChange={onTaxValueChange}
      onTaxLabelChange={onTaxLabelChange}
    />

    <TotalRow balanceDue={balanceDue} implausible={totalImplausible} />

    <PaymentTermsBlock
      override={state.payment_terms_override}
      profileDefault={profileDefaultPaymentTerms}
      onChange={onChangePaymentTermsOverride}
    />

    <CustomerNotesBlock
      override={state.customer_visible_notes}
      profileDefault={profileFooterNotes}
      onChange={onChangeCustomerVisibleNotes}
    />
  </section>
);

const SectionDivider = () => (
  <div
    style={{
      borderTop: `1px dashed ${COLORS.rule}`,
      margin: "14px -18px",
    }}
  />
);

/* ========================================================================== */
/*  Editable primitive — contenteditable wrapper                                */
/* ========================================================================== */

const Editable = ({
  value,
  placeholder,
  ghostText, // shown via CSS :empty::before when value is null/""
  numeric = false,
  prefix = "",
  suffix = "",
  visual, // 'plain' | 'warn' | 'low' | 'edited'
  implausible = false, // suspiciously-low-amount signal — outlines unless a
                       // stronger LLM warn/low signal is already shown
  active = false,
  onCommit,
  registerRef,
  testId,
  fontFamily = "'Inter', sans-serif",
  fontSize = 16,
  fontWeight = 500,
  textColor = COLORS.text,
  align = "left",
  minWidth, // optional — only set when layout truly needs it
}) => {
  const ref = useRef(null);
  const [justEdited, setJustEdited] = useState(false);

  // Push the value into the DOM only when not focused (avoids caret jumps).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    const display = value === null || value === "" ? "" : `${prefix}${value}${suffix}`;
    if (el.textContent !== display) el.textContent = display;
  }, [value, prefix, suffix]);

  // External focus request (Fix N items walk).
  useEffect(() => {
    if (!active) return;
    const el = ref.current;
    if (!el) return;
    setTimeout(() => {
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }, 50);
  }, [active]);

  const commit = () => {
    const el = ref.current;
    if (!el) return;
    let text = (el.textContent || "").trim();
    if (text.startsWith(prefix)) text = text.slice(prefix.length);
    if (suffix && text.endsWith(suffix)) text = text.slice(0, -suffix.length);
    text = text.trim();
    let next = text === "" ? null : text;
    if (numeric && next !== null) {
      const cleaned = String(next).replace(/[^0-9.\-]/g, "");
      const n = Number(cleaned);
      next = Number.isFinite(n) ? n : null;
    }
    if (next !== value) {
      onCommit(next);
      setJustEdited(true);
      setTimeout(() => setJustEdited(false), 1500);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const isEmpty = value === null || value === "";

  // Visual states → styling. Empty fields stay light-weight (no bg, no
  // min-width) so the italic CSS placeholder reads like ghost text, not
  // a button. The justEdited / warn states keep their subtle treatments.
  let leftBorderColor = "transparent";
  let bg = "transparent";
  if (justEdited) {
    leftBorderColor = COLORS.ok;
    bg = "rgba(74,222,128,0.06)";
  } else if (visual === "warn") {
    leftBorderColor = COLORS.warn;
    bg = "rgba(245,184,0,0.06)";
  } else if (visual === "low" && !isEmpty) {
    // value present but low confidence — show the warn left-border so the user
    // knows to double-check, but skip the heavy bg.
    leftBorderColor = COLORS.warn;
  }

  // Implausibility outline. Only when no stronger LLM signal is already on the
  // field — confidence/ambiguity wins to avoid double-marking, and the green
  // justEdited flash gets to play out uninterrupted.
  const showImplausibleOutline =
    implausible &&
    !justEdited &&
    visual !== "warn" &&
    visual !== "low";

  // Placeholder shown via CSS :empty::before so we never have real text inside
  // the contenteditable when it should look "empty".
  const placeholderText = isEmpty ? (ghostText || placeholder || "") : "";

  return (
    <span
      ref={(el) => {
        ref.current = el;
        if (registerRef) registerRef(el);
      }}
      data-testid={testId}
      data-ledger-editable=""
      data-placeholder={placeholderText}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      onBlur={commit}
      onKeyDown={onKeyDown}
      inputMode={numeric ? "decimal" : "text"}
      style={{
        display: "inline-block",
        minWidth: minWidth || undefined,
        padding: "6px 8px",
        borderRadius: 6,
        borderLeft: `3px solid ${leftBorderColor}`,
        backgroundColor: bg,
        color: textColor,
        fontFamily,
        fontSize,
        fontWeight,
        textAlign: align,
        outline: showImplausibleOutline ? `1.5px solid ${COLORS.warn}` : "none",
        outlineOffset: 0,
        cursor: "text",
        transition:
          "background-color 240ms ease, border-color 240ms ease",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    />
  );
};

/* ========================================================================== */
/*  FieldRow                                                                   */
/* ========================================================================== */

function visualForField(field, ambiguities, key) {
  if (field.edited) return "plain";
  const isEmpty = field.value === null || field.value === "";
  if (field.required && isEmpty) return "low";
  if (field.confidence === "low") return "low";
  const matched = ambiguities.some((a) => ambiguityMatchesField(a, key));
  if (field.confidence === "medium" || matched) return "warn";
  return "plain";
}

const FieldRow = ({
  fieldKey,
  label,
  field,
  ambiguities,
  placeholder,
  ghostText,
  active,
  registerRef,
  onCommit,
}) => {
  const visual = visualForField(field, ambiguities, fieldKey);
  return (
    <div
      data-testid={`review-fieldrow-${fieldKey}`}
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 12,
        padding: "8px 0",
      }}
    >
      <span
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: COLORS.faint,
          width: 76,
          flex: "0 0 auto",
        }}
      >
        {label}
      </span>
      <div style={{ flex: 1 }}>
        <Editable
          value={field.value}
          placeholder={placeholder}
          ghostText={ghostText}
          visual={visual}
          active={active}
          onCommit={onCommit}
          registerRef={registerRef}
          testId={`review-field-${fieldKey}`}
          fontFamily="'Instrument Serif', Georgia, serif"
          fontSize={20}
          fontWeight={400}
          textColor={COLORS.text}
        />
      </div>
    </div>
  );
};

/* ========================================================================== */
/*  LaborBlock                                                                 */
/* ========================================================================== */

/* ========================================================================== */
/*  CustomerBlock — name (required) + phone + email + address + company toggle */
/* ========================================================================== */

const CustomerBlock = ({ customer, ambiguities, rowRefs, activeFieldKey, onCommit }) => {
  // Company is optional and rare — collapsed by default unless extracted.
  const [companyExpanded, setCompanyExpanded] = useState(
    !!(customer.company && customer.company.value),
  );
  return (
    <div data-testid="review-customer-block">
      <BlockHeader>Bill to</BlockHeader>
      <FieldRow
        fieldKey="customer.name"
        label="Name"
        field={customer.name}
        ambiguities={ambiguities}
        placeholder="customer name"
        ghostText="Tap to add customer name →"
        active={activeFieldKey === "customer.name"}
        registerRef={(el) => (rowRefs.current["customer.name"] = el)}
        onCommit={(v) => onCommit("customer.name", v)}
      />
      <FieldRow
        fieldKey="customer.phone"
        label="Phone"
        field={customer.phone}
        ambiguities={ambiguities}
        placeholder="phone"
        ghostText="phone (optional)"
        active={activeFieldKey === "customer.phone"}
        registerRef={(el) => (rowRefs.current["customer.phone"] = el)}
        onCommit={(v) => onCommit("customer.phone", v ? formatPhone(v) : v)}
      />
      <FieldRow
        fieldKey="customer.email"
        label="Email"
        field={customer.email}
        ambiguities={ambiguities}
        placeholder="email"
        ghostText="email (optional)"
        active={activeFieldKey === "customer.email"}
        registerRef={(el) => (rowRefs.current["customer.email"] = el)}
        onCommit={(v) => onCommit("customer.email", v)}
      />
      <FieldRow
        fieldKey="customer.address"
        label="Address"
        field={customer.address}
        ambiguities={ambiguities}
        placeholder="billing address"
        ghostText="billing address (optional)"
        active={activeFieldKey === "customer.address"}
        registerRef={(el) => (rowRefs.current["customer.address"] = el)}
        onCommit={(v) => onCommit("customer.address", v)}
      />
      {companyExpanded || (customer.company && customer.company.value) ? (
        <FieldRow
          fieldKey="customer.company"
          label="Company"
          field={customer.company}
          ambiguities={ambiguities}
          placeholder="company name"
          ghostText="company (optional)"
          active={activeFieldKey === "customer.company"}
          registerRef={(el) => (rowRefs.current["customer.company"] = el)}
          onCommit={(v) => onCommit("customer.company", v)}
        />
      ) : (
        <button
          type="button"
          data-testid="review-customer-add-company"
          onClick={() => setCompanyExpanded(true)}
          style={{
            marginTop: 4,
            background: "transparent",
            border: "none",
            padding: "4px 0",
            color: COLORS.faint,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Plus size={12} /> add company
        </button>
      )}
    </div>
  );
};

/* ========================================================================== */
/*  JobBlock — site_address + description + service date + optional PO #      */
/* ========================================================================== */

const JobBlock = ({
  job,
  serviceDate,
  ambiguities,
  rowRefs,
  activeFieldKey,
  onCommit,
  onChangeServiceDate,
}) => {
  const [poExpanded, setPoExpanded] = useState(
    !!(job.po_number && job.po_number.value),
  );
  return (
    <div data-testid="review-job-block">
      <BlockHeader>Job</BlockHeader>
      <FieldRow
        fieldKey="job.site_address"
        label="Site"
        field={job.site_address}
        ambiguities={ambiguities}
        placeholder="job site address"
        ghostText="Same as billing"
        active={activeFieldKey === "job.site_address"}
        registerRef={(el) => (rowRefs.current["job.site_address"] = el)}
        onCommit={(v) => onCommit("job.site_address", v)}
      />
      <FieldRow
        fieldKey="job.description"
        label="Job"
        field={job.description}
        ambiguities={ambiguities}
        placeholder="what was done"
        ghostText="Tap to add job description"
        active={activeFieldKey === "job.description"}
        registerRef={(el) => (rowRefs.current["job.description"] = el)}
        onCommit={(v) => onCommit("job.description", v)}
      />
      <ServiceDateRow value={serviceDate} onChange={onChangeServiceDate} />
      {poExpanded || (job.po_number && job.po_number.value) ? (
        <FieldRow
          fieldKey="job.po_number"
          label="PO #"
          field={job.po_number}
          ambiguities={ambiguities}
          placeholder="PO number"
          ghostText="purchase order #"
          active={activeFieldKey === "job.po_number"}
          registerRef={(el) => (rowRefs.current["job.po_number"] = el)}
          onCommit={(v) => onCommit("job.po_number", v)}
        />
      ) : (
        <button
          type="button"
          data-testid="review-job-add-po"
          onClick={() => setPoExpanded(true)}
          style={{
            marginTop: 4,
            background: "transparent",
            border: "none",
            padding: "4px 0",
            color: COLORS.faint,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Plus size={12} /> add PO #
        </button>
      )}
    </div>
  );
};

// Native date input dressed to match the rest of the Review fields. Mobile
// browsers open the platform date picker on tap. `colorScheme: dark` gives
// Chromium-based desktops the dark-themed picker chrome.
const ServiceDateRow = ({ value, onChange }) => (
  <div
    data-testid="review-fieldrow-service_date"
    style={{
      display: "flex",
      alignItems: "baseline",
      gap: 12,
      padding: "8px 0",
    }}
  >
    <span
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 10,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: COLORS.faint,
        width: 76,
        flex: "0 0 auto",
      }}
    >
      Service
    </span>
    <input
      data-testid="review-field-service_date"
      type="date"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: "transparent",
        border: "none",
        borderBottom: `1px dashed ${COLORS.rule}`,
        color: COLORS.text,
        fontFamily: "'Instrument Serif', Georgia, serif",
        fontSize: 18,
        fontStyle: "italic",
        padding: "6px 0",
        outline: "none",
        colorScheme: "dark",
      }}
    />
  </div>
);

/* ========================================================================== */
/*  LineItemsBlock — flat editable list (replaces Labor + Materials + Other)  */
/* ========================================================================== */

const LINE_ITEM_CATEGORY_OPTIONS = [
  "labor",
  "materials",
  "service_call",
  "permit",
  "disposal",
];

const LineItemsBlock = ({
  lineItems,
  rowRefs,
  activeFieldKey,
  onCommit,
  onAdd,
  onRemove,
  onReorder,
}) => {
  // Native HTML5 drag is desktop-only. Mobile/touch falls back to the ▲/▼
  // buttons on every row (which work on every form factor).
  const [dragIndex, setDragIndex] = useState(null);
  const handleDragStart = (index) => (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(index)); }
    catch (err) { /* old Safari — dragIndex state still works */ }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (toIndex) => (e) => {
    e.preventDefault();
    let from = dragIndex;
    if (from === null) {
      const raw = Number(e.dataTransfer.getData("text/plain"));
      if (Number.isFinite(raw)) from = raw;
    }
    setDragIndex(null);
    if (from === null || from === toIndex) return;
    onReorder(from, toIndex);
  };
  const handleDragEnd = () => setDragIndex(null);

  // Discounts (negative amounts) are edited in the totals area, not here — keep
  // each row's original index so callbacks still target the right state entry.
  const visible = [];
  lineItems.forEach((item, i) => {
    if (Number(item.amount) >= 0) visible.push({ item, i });
  });

  return (
    <div data-testid="review-line-items-block">
      <BlockHeader>Items</BlockHeader>
      <datalist id="review-line-item-categories">
        {LINE_ITEM_CATEGORY_OPTIONS.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      {visible.length === 0 && (
        <div
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            color: COLORS.fainter,
            padding: "8px 0",
          }}
        >
          // no lines yet — tap "+ add line" to start
        </div>
      )}
      {visible.map(({ item, i }, vpos) => {
        // i = original index in state.line_items (kept for onCommit/onRemove,
        // testids, and the fix-walk). Reorder swaps with the adjacent *visible*
        // row's original index so a hidden discount line never gets in the way.
        const prevOrig = vpos > 0 ? visible[vpos - 1].i : null;
        const nextOrig = vpos < visible.length - 1 ? visible[vpos + 1].i : null;
        return (
          <LineItemRow
            key={item.id}
            index={i}
            isFirst={vpos === 0}
            isLast={vpos === visible.length - 1}
            item={item}
            activeFieldKey={activeFieldKey}
            rowRefs={rowRefs}
            onCommit={onCommit}
            onRemove={onRemove}
            onMoveUp={prevOrig != null ? () => onReorder(i, prevOrig) : undefined}
            onMoveDown={nextOrig != null ? () => onReorder(i, nextOrig) : undefined}
            onDragStart={handleDragStart(i)}
            onDragOver={handleDragOver}
            onDrop={handleDrop(i)}
            onDragEnd={handleDragEnd}
          />
        );
      })}
      <button
        type="button"
        data-testid="review-add-line-btn"
        onClick={onAdd}
        style={{
          marginTop: 8,
          background: "transparent",
          border: "none",
          color: COLORS.faint,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          cursor: "pointer",
          padding: "4px 0",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Plus size={12} /> add line
      </button>
    </div>
  );
};

// Reorder arrows: small chevrons, but a generous hit area (gloves — the
// 44px non-negotiable). Stacked vertically, each ~44 wide × 36 tall so the
// pair is a comfortable target without making the line row excessively tall.
const _ARROW_BTN_STYLE = {
  width: 44,
  height: 36,
  background: "transparent",
  border: "none",
  padding: 0,
  color: COLORS.fainter,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const LineItemRow = ({
  index,
  isFirst,
  isLast,
  item,
  activeFieldKey,
  rowRefs,
  onCommit,
  onRemove,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const [notesExpanded, setNotesExpanded] = useState(!!item.notes);
  const isNegative = Number(item.amount) < 0;
  const isLabor = String(item.category || "").toLowerCase() === "labor";

  return (
    <div
      data-testid={`review-line-${index}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        padding: "10px 0 8px",
        borderBottom: `1px dashed ${COLORS.rule}`,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: "0 0 auto" }}>
          <button
            type="button"
            data-testid={`review-line-${index}-up`}
            aria-label="Move up"
            disabled={isFirst}
            onClick={onMoveUp}
            style={{
              ..._ARROW_BTN_STYLE,
              opacity: isFirst ? 0.3 : 1,
              cursor: isFirst ? "not-allowed" : "pointer",
            }}
          >
            <ChevronUp size={11} />
          </button>
          <button
            type="button"
            data-testid={`review-line-${index}-down`}
            aria-label="Move down"
            disabled={isLast}
            onClick={onMoveDown}
            style={{
              ..._ARROW_BTN_STYLE,
              opacity: isLast ? 0.3 : 1,
              cursor: isLast ? "not-allowed" : "pointer",
            }}
          >
            <ChevronDown size={11} />
          </button>
        </div>
        <span
          data-testid={`review-line-${index}-drag`}
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          title="Drag to reorder"
          style={{
            cursor: "grab",
            color: COLORS.fainter,
            padding: "2px 4px",
            display: "inline-flex",
            alignItems: "center",
            flex: "0 0 auto",
            userSelect: "none",
          }}
        >
          <GripVertical size={14} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Editable
            value={item.description || null}
            placeholder="description"
            ghostText="description"
            visual="plain"
            onCommit={(v) => onCommit(index, { description: v || "" })}
            testId={`review-line-${index}-description`}
            fontFamily="'Instrument Serif', Georgia, serif"
            fontSize={18}
            fontWeight={400}
          />
        </div>
        <RemoveRowBtn
          onClick={() => onRemove(index)}
          testId={`review-line-${index}-remove`}
          ariaLabel="Remove line"
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          flexWrap: "wrap",
          paddingLeft: 56,
        }}
      >
        <Editable
          value={item.quantity}
          placeholder="qty"
          ghostText="qty"
          numeric
          visual="plain"
          onCommit={(v) => onCommit(index, { quantity: v })}
          testId={`review-line-${index}-quantity`}
          fontFamily="'Instrument Serif', Georgia, serif"
          fontSize={16}
          align="right"
          minWidth={32}
        />
        <input
          data-testid={`review-line-${index}-unit`}
          type="text"
          value={item.unit || ""}
          onChange={(e) => onCommit(index, { unit: e.target.value || null })}
          placeholder="unit"
          maxLength={8}
          style={{
            width: 50,
            background: "transparent",
            border: "none",
            borderBottom: `1px dashed ${COLORS.rule}`,
            color: COLORS.text,
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: 14,
            outline: "none",
            padding: "2px 4px",
            textAlign: "center",
          }}
        />
        <span style={{ color: COLORS.faint, fontSize: 13 }}>×</span>
        <Editable
          value={item.unit_price}
          placeholder="rate"
          ghostText="rate"
          numeric
          prefix="$"
          implausible={isLabor && isImplausibleAmount(item.unit_price, 10)}
          visual="plain"
          onCommit={(v) => onCommit(index, { unit_price: v })}
          testId={`review-line-${index}-unit_price`}
          fontFamily="'Instrument Serif', Georgia, serif"
          fontSize={16}
          align="right"
        />
        <span style={{ color: COLORS.faint, fontSize: 13, marginLeft: "auto" }}>=</span>
        <Editable
          value={item.amount}
          placeholder="amount"
          ghostText="amount"
          numeric
          prefix="$"
          implausible={item.amount > 0 && isImplausibleAmount(item.amount, 1)}
          visual="plain"
          active={activeFieldKey === `line_item:${index}:amount`}
          onCommit={(v) => onCommit(index, { amount: v })}
          registerRef={(el) => (rowRefs.current[`line_item:${index}:amount`] = el)}
          testId={`review-line-${index}-amount`}
          fontFamily="'Instrument Serif', Georgia, serif"
          fontSize={18}
          fontWeight={500}
          align="right"
          textColor={isNegative ? COLORS.red : COLORS.text}
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          paddingLeft: 56,
        }}
      >
        <input
          data-testid={`review-line-${index}-category`}
          type="text"
          list="review-line-item-categories"
          value={item.category || ""}
          onChange={(e) => onCommit(index, { category: e.target.value || null })}
          placeholder="category"
          style={{
            background: "transparent",
            border: "none",
            borderBottom: `1px dashed ${COLORS.rule}`,
            color: COLORS.faint,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.08em",
            textTransform: "lowercase",
            outline: "none",
            padding: "2px 4px",
            minWidth: 100,
            maxWidth: 160,
          }}
        />
        {item.notes || notesExpanded ? (
          <div style={{ flex: 1, minWidth: 120 }}>
            <Editable
              value={item.notes}
              placeholder="note"
              ghostText="note"
              visual="plain"
              onCommit={(v) => onCommit(index, { notes: v })}
              testId={`review-line-${index}-notes`}
              fontFamily="'Inter', sans-serif"
              fontSize={12}
              fontWeight={400}
              textColor={COLORS.faint}
            />
          </div>
        ) : (
          <button
            type="button"
            data-testid={`review-line-${index}-add-note`}
            onClick={() => setNotesExpanded(true)}
            style={{
              background: "transparent",
              border: "none",
              color: COLORS.fainter,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              cursor: "pointer",
              padding: "2px 0",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Plus size={11} /> note
          </button>
        )}
      </div>
    </div>
  );
};

/* ========================================================================== */
/*  PaymentTermsBlock — always-inline, directly editable per-invoice terms      */
/*  No expand/override click: the Net-N inputs are shown directly, pre-filled    */
/*  with the profile default as placeholder. Typing makes it a per-invoice       */
/*  override; Reset returns to the saved default.                                */
/* ========================================================================== */

const PaymentTermsBlock = ({ override, profileDefault, onChange }) => {
  const usingOverride =
    !!override &&
    override.days !== null &&
    override.days !== "" &&
    override.days !== undefined;
  const effective = usingOverride
    ? override
    : profileDefault && profileDefault.days !== null
      ? profileDefault
      : null;
  const effectiveLabel = effective
    ? effective.label && String(effective.label).trim()
      ? String(effective.label).trim()
      : Number(effective.days) === 0
        ? "Due on receipt"
        : `Net ${Number(effective.days)}`
    : null;

  return (
    <div
      data-testid="review-payment-terms-block"
      style={{
        marginLeft: -18,
        marginRight: -18,
        padding: "10px 18px 14px",
        borderTop: `1px dashed ${COLORS.rule}`,
      }}
    >
      {/* Header: label + state tag + reset */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.06em",
            color: COLORS.fainter,
          }}
        >
          PAYMENT TERMS
        </span>
        {usingOverride ? (
          <span
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 9,
              letterSpacing: "0.14em",
              color: COLORS.yellow,
              border: `1px solid ${COLORS.yellow}`,
              borderRadius: 999,
              padding: "1px 6px",
            }}
          >
            THIS INVOICE
          </span>
        ) : (
          <span
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10,
              color: COLORS.fainter,
            }}
          >
            {effective ? "your default" : "not set"}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {usingOverride && (
          <button
            type="button"
            data-testid="review-payment-terms-clear"
            onClick={() => onChange(null)}
            style={{
              background: "transparent",
              border: "none",
              color: COLORS.faint,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: "pointer",
              padding: 0,
            }}
          >
            Reset
          </button>
        )}
      </div>

      {/* Always-visible edit row */}
      <div
        data-testid="review-payment-terms-edit"
        style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
      >
        <span
          style={{
            color: COLORS.faint,
            fontSize: 12,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          }}
        >
          Net
        </span>
        <input
          data-testid="review-payment-terms-days"
          type="text"
          inputMode="numeric"
          value={override?.days ?? ""}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9]/g, "");
            if (raw === "") {
              onChange(null);
              return;
            }
            onChange({ days: parseInt(raw, 10), label: override?.label || null });
          }}
          placeholder={profileDefault?.days != null ? String(profileDefault.days) : "15"}
          style={{
            width: 60,
            background: "transparent",
            border: "none",
            borderBottom: `1px dashed ${COLORS.rule}`,
            color: COLORS.text,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 14,
            textAlign: "right",
            outline: "none",
            padding: "4px 0",
          }}
        />
        <span style={{ color: COLORS.faint, fontSize: 12 }}>days</span>
        <input
          data-testid="review-payment-terms-label"
          type="text"
          value={override?.label || ""}
          onChange={(e) =>
            onChange({
              days:
                override?.days != null
                  ? override.days
                  : profileDefault?.days != null
                    ? profileDefault.days
                    : 0,
              label: e.target.value || null,
            })
          }
          placeholder="Optional label (e.g. Due on receipt)"
          maxLength={32}
          style={{
            flex: 1,
            minWidth: 140,
            background: "transparent",
            border: "none",
            borderBottom: `1px dashed ${COLORS.rule}`,
            color: COLORS.text,
            fontFamily: "'Inter', sans-serif",
            fontSize: 13,
            outline: "none",
            padding: "4px 0",
          }}
        />
      </div>

      {/* Hint — what shows on the PDF + inherit/override clarity */}
      <div
        style={{
          marginTop: 8,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          color: COLORS.fainter,
          lineHeight: 1.5,
        }}
      >
        {usingOverride
          ? `Shows "${effectiveLabel}" on this invoice — your saved default is unchanged`
          : effective
            ? `Using your default (${effectiveLabel}) — type to change just this invoice`
            : "No default set — enter terms here, or save a default in Settings"}
      </div>
    </div>
  );
};

/* ========================================================================== */
/*  CustomerNotesBlock — always-inline, directly editable per-invoice notes      */
/*  Tri-state on the wire: null = inherit profile footer notes, "" = suppress    */
/*  (no NOTES block on the PDF), "text" = override. The textarea shows the        */
/*  effective text directly; editing makes it a per-invoice override; Reset        */
/*  returns to the saved default.                                                 */
/* ========================================================================== */

const CustomerNotesBlock = ({ override, profileDefault, onChange }) => {
  const isOverridden = override !== null && override !== undefined;
  const value = isOverridden ? String(override) : (profileDefault || "");

  return (
    <div
      data-testid="review-customer-notes-block"
      style={{
        marginLeft: -18,
        marginRight: -18,
        padding: "10px 18px 14px",
        borderTop: `1px dashed ${COLORS.rule}`,
      }}
    >
      {/* Header: label + state tag + reset */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.06em",
            color: COLORS.fainter,
          }}
        >
          // NOTES
        </span>
        {isOverridden ? (
          <span
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 9,
              letterSpacing: "0.14em",
              color: COLORS.yellow,
              border: `1px solid ${COLORS.yellow}`,
              borderRadius: 999,
              padding: "1px 6px",
            }}
          >
            THIS INVOICE
          </span>
        ) : (
          <span
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10,
              color: COLORS.fainter,
            }}
          >
            {profileDefault ? "your default" : "optional"}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {isOverridden && (
          <button
            type="button"
            data-testid="review-customer-notes-reset"
            onClick={() => onChange(null)}
            style={{
              background: "transparent",
              border: "none",
              color: COLORS.faint,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: "pointer",
              padding: 0,
            }}
          >
            Reset to default
          </button>
        )}
      </div>

      <textarea
        data-testid="review-customer-notes-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="// note your customer sees on this invoice (optional)"
        rows={3}
        style={{
          width: "100%",
          backgroundColor: "#1a1917",
          border: `1px solid ${isOverridden ? COLORS.yellow : COLORS.rule}`,
          borderRadius: 8,
          padding: "10px 12px",
          color: COLORS.text,
          fontFamily: "'Inter', sans-serif",
          fontSize: 13,
          lineHeight: 1.4,
          outline: "none",
          resize: "vertical",
          boxSizing: "border-box",
        }}
      />

      {/* Hint — what shows on the PDF + inherit/override clarity */}
      <div
        style={{
          marginTop: 8,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          color: COLORS.fainter,
          lineHeight: 1.5,
        }}
      >
        {isOverridden
          ? value.trim() === ""
            ? "Hidden on this invoice — no notes block on the PDF"
            : "Shows on this invoice only — your saved default is unchanged"
          : profileDefault
            ? "Using your saved default — edit to change just this invoice"
            : "No saved default — type a note for this invoice, or leave blank"}
      </div>
    </div>
  );
};

/* ========================================================================== */
/*  DepositBlock — collapsed by default; expands to amount + method            */
/* ========================================================================== */

const DepositBlock = ({ deposit, onChange }) => {
  const [expanded, setExpanded] = useState(!!deposit);

  // Re-sync expanded if state changes externally (e.g. extraction seeded a
  // deposit after first render).
  useEffect(() => {
    if (deposit) setExpanded(true);
  }, [deposit]);

  if (!expanded) {
    return (
      <div style={{ paddingTop: 14 }}>
        <button
          type="button"
          data-testid="review-deposit-add-btn"
          onClick={() => setExpanded(true)}
          style={{
            background: "transparent",
            border: "none",
            color: COLORS.faint,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            cursor: "pointer",
            padding: "4px 0",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Plus size={12} /> deposit received
        </button>
      </div>
    );
  }

  const amount = deposit?.amount;
  const method = deposit?.method || "";

  return (
    <div
      data-testid="review-deposit-block"
      style={{
        marginTop: 14,
        padding: "12px 0",
        borderTop: `1px dashed ${COLORS.rule}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <BlockHeader>Deposit received</BlockHeader>
        <button
          type="button"
          data-testid="review-deposit-remove-btn"
          aria-label="Remove deposit"
          onClick={() => {
            onChange(null);
            setExpanded(false);
          }}
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            color: COLORS.fainter,
            cursor: "pointer",
            display: "inline-flex",
          }}
        >
          <X size={14} />
        </button>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
          <Editable
            value={amount ?? null}
            placeholder="amount"
            ghostText="amount"
            numeric
            prefix="$"
            visual="plain"
            onCommit={(v) =>
              onChange(
                v === null || v === ""
                  ? null
                  : { amount: Number(v), method: deposit?.method || null },
              )
            }
            testId="review-deposit-amount"
            fontFamily="'Instrument Serif', Georgia, serif"
            fontSize={20}
            fontWeight={400}
            align="right"
          />
        </div>
        <input
          data-testid="review-deposit-method"
          type="text"
          value={method}
          onChange={(e) =>
            onChange({
              amount: Number.isFinite(Number(amount)) ? Number(amount) : 0,
              method: e.target.value || null,
            })
          }
          placeholder="method (Venmo, cash, …)"
          style={{
            flex: 1,
            minWidth: 140,
            background: "transparent",
            border: "none",
            borderBottom: `1px dashed ${COLORS.rule}`,
            color: COLORS.text,
            fontFamily: "'Inter', sans-serif",
            fontSize: 13,
            outline: "none",
            padding: "6px 0",
          }}
        />
      </div>
    </div>
  );
};

const RemoveRowBtn = ({ onClick, testId, ariaLabel }) => (
  <button
    type="button"
    data-testid={testId}
    aria-label={ariaLabel}
    onClick={onClick}
    data-ledger-remove-row=""
    style={{
      background: "transparent",
      border: "none",
      width: 44,
      height: 44,
      padding: 0,
      color: COLORS.fainter,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "0 0 auto",
      transition: "color 160ms ease",
    }}
  >
    <X size={15} />
  </button>
);

/* ========================================================================== */
/*  SubtotalTaxBlock + TotalRow                                                */
/* ========================================================================== */

const SubtotalTaxBlock = ({
  subtotal,
  discountTotal,
  onDiscountChange,
  depositAmount,
  taxEnabled,
  taxMode,
  taxValue,
  taxLabel,
  taxAmount,
  totalDue,
  onToggleTaxEnabled,
  onSwitchTaxMode,
  onTaxValueChange,
  onTaxLabelChange,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [addingDiscount, setAddingDiscount] = useState(false);
  // subtotal is net of the discount; show the gross items sum and let the
  // editable DISCOUNT row bring it down. discountTotal is non-positive.
  const grossSubtotal = +(subtotal - (discountTotal || 0)).toFixed(2);
  const hasDiscount = Number.isFinite(discountTotal) && discountTotal !== 0;
  // Show the input once a discount exists OR the user tapped "+ add discount"
  // (the line isn't created until they commit a positive amount).
  const showDiscountInput = hasDiscount || addingDiscount;
  // The discount field uses a LOCAL draft string, committed on blur — NOT a
  // value bound straight to the rounded discountTotal. A controlled value
  // recomputed from numeric state strips a trailing "." mid-typing (you could
  // never enter $1.50) and unmounts the field the instant you clear it. The
  // draft syncs from the canonical value only while the field isn't focused.
  const [discountDraft, setDiscountDraft] = useState(
    hasDiscount ? String(Math.abs(discountTotal)) : "",
  );
  const discountFocused = useRef(false);
  useEffect(() => {
    if (!discountFocused.current) {
      setDiscountDraft(hasDiscount ? String(Math.abs(discountTotal)) : "");
    }
  }, [hasDiscount, discountTotal]);
  const monoLabel = {
    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
    fontSize: 11,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: COLORS.faint,
  };
  return (
    <div
      data-testid="review-subtotal-tax-block"
      style={{
        marginTop: 14,
        paddingTop: 12,
        borderTop: `1px dashed ${COLORS.rule}`,
        marginLeft: -18,
        marginRight: -18,
        padding: "12px 18px 0",
      }}
    >
      {/* Subtotal row */}
      <div
        data-testid="review-subtotal-row"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          padding: "6px 0",
        }}
      >
        <span style={monoLabel}>Subtotal</span>
        <span
          data-testid="review-subtotal-amount"
          style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: 18,
            color: COLORS.text,
          }}
        >
          {fmtMoney(grossSubtotal)}
        </span>
      </div>

      {/* Discount — edited here in the totals area (not as a line item).
          Stored as a negative line so the wire math is unchanged. When none is
          set, a "+ add discount" affordance appears; once set, an inline
          amount input with a clear (×). */}
      {showDiscountInput ? (
        <div
          data-testid="review-discount-row"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            padding: "4px 0 6px",
            borderBottom: `1px dotted ${COLORS.rule}`,
          }}
        >
          <span style={monoLabel}>Discount</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: COLORS.red, fontSize: 16, fontFamily: "'Instrument Serif', Georgia, serif" }}>
              −$
            </span>
            <input
              data-testid="review-discount-amount"
              type="text"
              inputMode="decimal"
              value={discountDraft}
              onFocus={() => {
                discountFocused.current = true;
              }}
              onChange={(e) =>
                setDiscountDraft(e.target.value.replace(/[^0-9.]/g, ""))
              }
              onBlur={() => {
                discountFocused.current = false;
                onDiscountChange(discountDraft === "" ? null : discountDraft);
                if (discountDraft === "") setAddingDiscount(false);
              }}
              style={{
                width: 72,
                background: "transparent",
                border: "none",
                borderBottom: `1px dashed ${COLORS.rule}`,
                color: COLORS.red,
                fontFamily: "'Instrument Serif', Georgia, serif",
                fontSize: 16,
                textAlign: "right",
                outline: "none",
                padding: "2px 0",
              }}
            />
            <button
              type="button"
              data-testid="review-discount-clear"
              aria-label="Remove discount"
              onClick={() => {
                discountFocused.current = false;
                setDiscountDraft("");
                setAddingDiscount(false);
                onDiscountChange(null);
              }}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                marginLeft: 2,
                color: COLORS.fainter,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <X size={14} />
            </button>
          </span>
        </div>
      ) : (
        <div style={{ padding: "4px 0 2px" }}>
          <button
            type="button"
            data-testid="review-discount-add-btn"
            onClick={() => setAddingDiscount(true)}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              color: COLORS.faint,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Plus size={11} /> add discount
          </button>
        </div>
      )}

      {!taxEnabled ? (
        <div style={{ padding: "4px 0 2px" }}>
          <button
            type="button"
            data-testid="review-tax-add-btn"
            onClick={() => onToggleTaxEnabled(true)}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              color: COLORS.faint,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Plus size={11} /> add tax
          </button>
        </div>
      ) : (
        <div
          data-testid="review-tax-block"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            padding: "6px 0 4px",
          }}
        >
          {/* Header: TAX label + remove (×) */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={monoLabel}>Tax</span>
              <span style={{ position: "relative", display: "inline-flex" }}>
                <button
                  type="button"
                  data-testid="review-tax-tooltip-btn"
                  aria-label="Tax help"
                  onClick={() => setTooltipOpen((v) => !v)}
                  onBlur={() => setTimeout(() => setTooltipOpen(false), 100)}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    color: COLORS.fainter,
                    cursor: "help",
                    display: "inline-flex",
                  }}
                >
                  <HelpCircle size={12} />
                </button>
                {tooltipOpen && (
                  <span
                    data-testid="review-tax-tooltip"
                    role="tooltip"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      zIndex: 3,
                      width: 220,
                      padding: "8px 10px",
                      borderRadius: 8,
                      backgroundColor: "#1a1917",
                      border: `1px solid ${COLORS.rule}`,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 11,
                      color: "#d6d2c3",
                      lineHeight: 1.45,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                      textTransform: "none",
                      letterSpacing: "normal",
                    }}
                  >
                    Flat $ adds a fixed amount. Percentage % multiplies the
                    subtotal. Consult your accountant for jurisdiction rules.
                  </span>
                )}
              </span>
            </div>
            <button
              type="button"
              data-testid="review-tax-remove-btn"
              aria-label="Remove tax"
              onClick={() => onToggleTaxEnabled(false)}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                color: COLORS.fainter,
                cursor: "pointer",
                display: "inline-flex",
              }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Mode toggle: Flat $ | Percentage % */}
          <SegmentedTaxMode mode={taxMode} onSwitch={onSwitchTaxMode} />

          {/* Value + label inputs */}
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <div
              data-testid="review-tax-value-wrap"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                backgroundColor: "#1f1c17",
                border: `1px solid ${COLORS.rule}`,
                borderRadius: 8,
                padding: "8px 10px",
                flex: "0 0 110px",
                minWidth: 0,
              }}
            >
              {taxMode === "flat" && (
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: 13,
                    color: COLORS.faint,
                  }}
                >
                  $
                </span>
              )}
              <input
                data-testid="review-tax-value-input"
                type="text"
                inputMode="decimal"
                value={taxValue}
                onChange={(e) => onTaxValueChange(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder={taxMode === "flat" ? "0.00" : "8.25"}
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: COLORS.text,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 13,
                  textAlign: "right",
                  padding: 0,
                }}
              />
              {taxMode === "percentage" && (
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: 13,
                    color: COLORS.faint,
                  }}
                >
                  %
                </span>
              )}
            </div>
            <input
              data-testid="review-tax-label-input"
              type="text"
              value={taxLabel}
              onChange={(e) => onTaxLabelChange(e.target.value)}
              placeholder="Tax"
              maxLength={24}
              style={{
                flex: 1,
                minWidth: 0,
                backgroundColor: "#1f1c17",
                border: `1px solid ${COLORS.rule}`,
                borderRadius: 8,
                padding: "8px 10px",
                color: COLORS.text,
                fontFamily: "'Inter', sans-serif",
                fontSize: 13,
                outline: "none",
              }}
            />
          </div>

          {/* Computed amount row */}
          <div
            data-testid="review-tax-row"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              paddingTop: 2,
            }}
          >
            <span style={monoLabel}>{(taxLabel || "Tax").toUpperCase()}</span>
            <span
              data-testid="review-tax-amount"
              style={{
                fontFamily: "'Instrument Serif', Georgia, serif",
                fontSize: 18,
                color: COLORS.text,
              }}
            >
              {taxAmount === null || taxAmount === undefined ? "—" : fmtMoney(taxAmount)}
            </span>
          </div>
        </div>
      )}

      {/* Deposit received row — only when a deposit applies. Shown as a
          negative line so the math from totalDue to balanceDue is visible. */}
      {Number.isFinite(depositAmount) && depositAmount > 0 && (
        <div
          data-testid="review-deposit-row"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            padding: "6px 0 2px",
          }}
        >
          <span style={monoLabel}>Deposit received</span>
          <span
            data-testid="review-deposit-amount-row"
            style={{
              fontFamily: "'Instrument Serif', Georgia, serif",
              fontSize: 16,
              color: COLORS.red,
            }}
          >
            {fmtMoney(-Number(depositAmount))}
          </span>
        </div>
      )}
    </div>
  );
};

const SegmentedTaxMode = ({ mode, onSwitch }) => {
  const baseBtn = {
    flex: 1,
    padding: "8px 10px",
    borderRadius: 8,
    border: `1px solid ${COLORS.rule}`,
    backgroundColor: "transparent",
    color: COLORS.faint,
    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
    fontSize: 11,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    cursor: "pointer",
    transition: "color 140ms ease, background-color 140ms ease, border-color 140ms ease",
  };
  const active = {
    color: "#0f0e0c",
    backgroundColor: COLORS.yellow,
    borderColor: COLORS.yellow,
  };
  return (
    <div
      data-testid="review-tax-mode-toggle"
      role="tablist"
      style={{ display: "flex", gap: 6 }}
    >
      <button
        type="button"
        role="tab"
        aria-selected={mode === "flat"}
        data-testid="review-tax-mode-flat"
        onClick={() => onSwitch("flat")}
        style={{ ...baseBtn, ...(mode === "flat" ? active : {}) }}
      >
        Flat $
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === "percentage"}
        data-testid="review-tax-mode-pct"
        onClick={() => onSwitch("percentage")}
        style={{ ...baseBtn, ...(mode === "percentage" ? active : {}) }}
      >
        Percentage %
      </button>
    </div>
  );
};

// The dominant total row at the bottom of the invoice card. After G6 this
// displays BALANCE DUE (total_due − deposit − payments) instead of
// total_due, since the spec says "balance_due as the main number". The
// deposit row in SubtotalTaxBlock above already shows the deduction; this
// row is what the contractor reads as "what the customer owes me right now".
const TotalRow = ({ balanceDue, implausible = false }) => (
  <div
    data-testid="review-total-row"
    data-implausible={implausible ? "true" : undefined}
    style={{
      borderTop: implausible
        ? `1.5px solid ${COLORS.warn}`
        : `2px solid ${COLORS.yellow}`,
      borderBottom: implausible ? `1.5px solid ${COLORS.warn}` : undefined,
      marginTop: 16,
      marginLeft: -18,
      marginRight: -18,
      padding: "16px 18px 18px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: implausible ? "transparent" : "rgba(232,220,45,0.04)",
    }}
  >
    <span
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 11,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        color: COLORS.faint,
      }}
    >
      Balance Due
    </span>
    <span
      data-testid="review-total-amount"
      style={{
        fontFamily: "'Instrument Serif', Georgia, serif",
        fontSize: 28,
        color: implausible ? COLORS.warn : COLORS.yellow,
      }}
    >
      {fmtMoney(balanceDue)}
    </span>
  </div>
);

const BlockHeader = ({ children }) => (
  <div
    style={{
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: 10,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      color: COLORS.faint,
      marginBottom: 4,
    }}
  >
    {children}
  </div>
);

/* ========================================================================== */
/*  ActionBar (sticky bottom)                                                  */
/* ========================================================================== */

const ActionBar = ({ issueCount, missingRequiredCount, allClean, generating, generateError, onFix, onSkip, onGenerate }) => {
  const hasMissingRequired = missingRequiredCount > 0;

  let primary;
  if (allClean) {
    primary = (
      <button
        data-testid="review-generate-btn"
        onClick={onGenerate}
        disabled={generating}
        style={{
          flex: 1,
          padding: "16px 18px",
          borderRadius: 999,
          border: "none",
          backgroundColor: COLORS.yellow,
          color: "#0f0e0c",
          fontFamily: "'Inter', sans-serif",
          fontWeight: 800,
          fontSize: 14,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          cursor: generating ? "progress" : "pointer",
          boxShadow: "0 24px 60px -18px rgba(232,220,45,0.5)",
          opacity: generating ? 0.8 : 1,
        }}
      >
        {generating ? "Generating…" : "Generate PDF →"}
      </button>
    );
  } else {
    primary = (
      <button
        data-testid="review-fix-btn"
        onClick={hasMissingRequired ? undefined : onFix}
        disabled={hasMissingRequired ? false : false}
        style={{
          flex: 1,
          padding: "16px 18px",
          borderRadius: 999,
          border: "none",
          backgroundColor: COLORS.warn,
          color: "#0f0e0c",
          fontFamily: "'Inter', sans-serif",
          fontWeight: 800,
          fontSize: 14,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          cursor: "pointer",
          opacity: 1,
        }}
        onClickCapture={(e) => {
          // If missing required, the button is "live" but doesn't navigate to
          // generate — it still kicks off the fix walk.
          if (hasMissingRequired) {
            e.preventDefault();
            onFix();
          }
        }}
      >
        Fix {issueCount} item{issueCount === 1 ? "" : "s"} →
      </button>
    );
  }

  return (
    <div
      data-testid="review-actionbar"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        // Add the iPhone home-indicator inset so the buttons aren't under it.
        padding: "14px 18px calc(18px + env(safe-area-inset-bottom))",
        background:
          "linear-gradient(to top, rgba(15,14,12,0.98) 60%, rgba(15,14,12,0.7) 90%, rgba(15,14,12,0))",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 680,
        margin: "0 auto",
      }}
    >
      {generateError && (
        <div
          data-testid="review-generate-error"
          role="alert"
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: `1px solid ${COLORS.red}`,
            backgroundColor: "rgba(229,75,60,0.08)",
            color: "#ffb3ab",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            lineHeight: 1.5,
            textAlign: "center",
          }}
        >
          {generateError}
        </div>
      )}
      <div style={{ display: "flex", gap: 10 }}>
        {primary}
        {!allClean && !hasMissingRequired && (
          <button
            data-testid="review-skip-btn"
            onClick={onSkip}
            style={{
              padding: "16px 18px",
              borderRadius: 999,
              border: `1px solid ${COLORS.rule}`,
              backgroundColor: "transparent",
              color: COLORS.faint,
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Skip
          </button>
        )}
      </div>
    </div>
  );
};
